import { logout } from './logout';
import { refreshToken } from './refreshToken';
import { getAppStore } from '../store/storeRef';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { config } from '../config';

/**
 * A fetchApi override for OpenAPI’s TS-fetch client.
 * 1) Returns the raw Response so the generator can do .json() → DTO.
 * 2) On 401, tries a token refresh and retries once.
 * 3) On refresh failure or other 401, logs out.
 * 4) On 500, logs out via your existing logic.
 */
export const openApiFetchWrapper = async (
  input: RequestInfo,
  init: RequestInit = {},
): Promise<Response> => {
  // inject current access token header
  const token =
    getAppStore().getState().tokenState[AppIdentifiers.App]?.accessToken;
  init.headers = {
    ...(init.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // Members can roll back to an older build, so every request says which one
    // it came from. /client-support is what turns that into a warning.
    'X-App-Version': config.VERSION,
  };

  let response: Response;
  try {
    response = await fetch(input, init);
  } catch {
    console.error('Network error or no response received');
    throw new Error('NetworkError');
  }

  // if 401 → try refresh once
  if (response.status === 401) {
    console.warn('401 received; attempting token refresh');
    try {
      await refreshToken(); // your existing refresh flow must update the store
      const newToken =
        getAppStore().getState().tokenState[AppIdentifiers.App]?.accessToken;
      // retry with new token
      init.headers = {
        ...(init.headers as Record<string, string>),
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
      };
      response = await fetch(input, init);
    } catch {
      console.error('Refresh failed; logging out');
      logout();
      // let the client see the 401 (or you could throw a special error)
      throw new Error('Unauthorized');
    }
  }

  // handle 500
  if (response.status === 500) {
    console.error('Server error (500)');
    // still return response so client can throw its own ApiError if desired
  }

  // any other non-OK status
  if (!response.ok) {
    console.error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response;
};
